import * as Yup from 'yup';
import { useCallback, useEffect } from 'react';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { DatePicker, LoadingButton } from '@mui/lab';
import {
  Card,
  Grid,
  Stack,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  CardHeader,
  Autocomplete,
  Checkbox
} from '@mui/material';
import { axios } from 'utils/axios';
import { FormHelperText } from '@mui/material';
import { AxiosError } from 'axios';
import { useLocation } from 'react-router-dom';
import { DriverIcons } from 'utils/Icons/icons';
import InfoCard from 'components/common/InfoCard';
import moment from 'moment';

type DriverNewFormProps = {
  isEdit: boolean;
  isDetails: boolean;
  currentUser?: any;
};

export default function DriverCreateForm({ isEdit, isDetails, currentUser }: DriverNewFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { enqueueSnackbar } = useSnackbar();
  const [contacts, setContacts] = useState<any>([]);
  const [haulier, setHaulier] = useState<any>([]);
  const [client, setClient] = useState<any>([]);
  const [vehicleType, setVehicleType] = useState<any>([]);
  const [companies, setCompanies] = useState<any>([]);
  const NewUserSchema = Yup.object().shape({
    FullName: Yup.string().required('Full Name is required'),
    Mobile: Yup.string(),
    PersonnelId: Yup.number().nullable(),
    ExternalId: Yup.number().nullable(),
    Email: Yup.string(),
    Client: Yup.string().nullable(),
    ClientId: Yup.number().nullable(),
    Haulier: Yup.string().nullable(),
    HaulierId: Yup.number().nullable(),
    InternalExternal: Yup.string().required('Internal/External field is required'),
    AccessCode: Yup.string(),
    PIN: Yup.string().required('PIN is required'),
    DefaultVehicleId: Yup.number().nullable(),
    Inducted: Yup.boolean(),
    defaultCompanyId: Yup.number()
      .nullable()
      .when('InternalExternal', {
        is: 'I',
        then: Yup.number().required('Company is required for Internal users'),
        otherwise: Yup.number().nullable().notRequired()
      }),

    Available: Yup.boolean().nullable(),
    InductedDate: Yup.date()
      .nullable() // Allow null or empty values
      .when('Inducted', {
        is: true,
        then: Yup.date().required('Inducted Date is required').typeError('Invalid date format'), // Ensures proper date format validation
        otherwise: Yup.date().nullable().notRequired()
      }),
    InductedBy: Yup.string()

      .nullable()
      .when('Inducted', {
        is: true,
        then: Yup.string().required('Inducted By is required'),
        otherwise: Yup.string().nullable().notRequired()
      }),

    Description: Yup.string().nullable(),
    CompanyID: Yup.number().nullable(),
    ignoreDuplicates: Yup.boolean()
  });

  // Formik setup
  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      FullName: currentUser?.FullName || '',
      Mobile: currentUser?.Mobile || '',
      Email: currentUser?.Email || '',
      Client: currentUser?.Client || '',
      ClientId: currentUser?.ClientId || null,
      Haulier: currentUser?.Haulier || '',
      HaulierId: currentUser?.HaulierId || null,
      PersonnelId: currentUser?.PersonnelId || null,
      ExternalId: currentUser?.ExternalId || '',
      InternalExternal: currentUser?.InternalExternal || '',
      AccessCode: currentUser?.AccessCode || '',
      PIN: currentUser?.PIN || '',
      DefaultVehicleId: currentUser?.DefaultVehicleId || null,
      Inducted: currentUser?.Inducted || false,
      Available: currentUser?.Available === false ? false : true,
      InductedDate: currentUser?.InductedDate
        ? new Date(currentUser.InductedDate).toISOString()
        : null,
      InductedBy: currentUser?.InductedBy || '',
      Description: currentUser?.Description || '',
      CompanyID: currentUser?.CompanyID || null,
      defaultCompanyId: currentUser.defaultCompanyId || null,
      ignoreDuplicates: false
    },
    validationSchema: isDetails ? null : NewUserSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) {
        enqueueSnackbar('Please correct the errors before submitting.', { variant: 'error' });
        setSubmitting(false);
        return;
      }

      try {
        const payload = {
          ...values,
          InductedDate: values.InductedDate ? new Date(values.InductedDate).toISOString() : '',
          ignoreDuplicates: values.ignoreDuplicates
        };
        if (isEdit) {
          await axios.put(`/rest/api/drivers/${currentUser.Id}`, payload);
          enqueueSnackbar('Update success', { variant: 'success' });
        } else {
          await axios.post('/rest/api/drivers', payload);
          enqueueSnackbar('Create success', { variant: 'success' });
        }
        navigate('/dashboard/drivers');
      } catch (error) {
        const axiosError = error as AxiosError<{
          success?: boolean;
          isDuplicate?: boolean;
          message?: string;
          details?: Array<{ Id: number; FullName: string; PIN: string; Deleted: boolean }>;
        }>;
        const data = axiosError.response?.data;
        if (data?.isDuplicate && data?.success === false) {
          const message = data.message || 'A duplicate driver already exists.';
          const details = data.details || [];
          enqueueSnackbar(
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {message}
              </Typography>
              {details.length > 0 && (
                <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 2 }}>
                  {details.map(
                    (d: { Id: number; FullName: string; PIN: string; Deleted: boolean }) => (
                      <li key={d.Id}>
                        <strong>{d.FullName}</strong> (PIN: {d.PIN}){d.Deleted && ' [Deleted]'}
                      </li>
                    )
                  )}
                </Box>
              )}
            </Box>,
            { variant: 'warning', autoHideDuration: 10000 }
          );
          setSubmitting(false);
          return;
        }
        console.error(error);
        enqueueSnackbar('An error occurred while submitting the form', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;
  const fetchContacts = async () => {
    try {
      const response1 = await axios.get('/rest/api/contacts/dropdown?isOwner=1');
      const response2 = await axios.get('/rest/api/contacts/dropdown?isClient=1');
      const response3 = await axios.get('/rest/api/contacts/dropdown?IsHaulier=1');
      const response4 = await axios.get('/rest/api/company/dropdown');
      const Companies = response4.data.data || [];
      setCompanies(Companies.map((el: any) => ({ value: el.name, id: el.id })));

      const allContacts = response1.data.data || [];
      const allClients = response2.data.data || [];
      const allHaulier = response3.data.data || [];
      setContacts(
        allContacts.map((el: any) => ({ value: el.Name, id: el.Id, accountRef: el.AccountRef }))
      );
      setClient(
        allClients.map((el: any) => ({ value: el.Name, id: el.Id, accountRef: el.AccountRef }))
      );
      setHaulier(
        allHaulier.map((el: any) => ({ value: el.Name, id: el.Id, accountRef: el.AccountRef }))
      );
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };
  const fetchVehiclesByOwner = async (ownerName: string) => {
    let filters = `Owner=${ownerName}`;
    try {
      const response = await axios.get(`/rest/api/vehicle-dropdown?${filters}`);
      const allVehicleTypes = response.data.data || [];

      const formattedVehicles = allVehicleTypes
        .filter((el: any) => el && el.Id && el.RegistrationPlate && el.Description)
        .map((el: any) => ({
          id: el.Id,
          registrationPlate: el.RegistrationPlate.trim(),
          description: el.Description.trim()
        }));

      setVehicleType(formattedVehicles);

      // Auto-select DefaultVehicleId if present
      if (formik.values.DefaultVehicleId) {
        const defaultVehicle = formattedVehicles.find(
          (vehicle: any) => vehicle.id === formik.values.DefaultVehicleId
        );
        if (defaultVehicle) {
          formik.setFieldValue('DefaultVehicleId', defaultVehicle.id);
        }
      }
    } catch (error) {
      console.error('Error fetching vehicle types:', error);
      setVehicleType([]); // Set an empty array if the request fails
    }
  };

  useEffect(() => {
    fetchContacts();
    // fetchAllVehicleTypes();
  }, []);
  useEffect(() => {
    if (formik.values.CompanyID) {
      fetchVehiclesByOwner(
        contacts.find((c: any) => c.id === formik.values.CompanyID)?.value || ''
      );
    }
  }, [formik.values.CompanyID]);

  return (
    <>
      {isDetails ? (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              {/* General Information */}
              <CardHeader title="General Information" />
              <Stack spacing={3} padding={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InfoCard
                      icon={DriverIcons.person}
                      title=" Full Name"
                      content={formik.values.FullName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoCard
                      icon={DriverIcons.phone}
                      title="Mobile"
                      content={formik.values.Mobile}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoCard
                      icon={DriverIcons.email}
                      title="Email"
                      content={formik.values.Email}
                    />
                  </Grid>
                </Grid>
              </Stack>

              {/* Access Code and PIN */}
              <CardHeader title="Access Code and PIN" />
              <Stack spacing={3} padding={3}>
                <Grid container spacing={2}>
                  {/* Access Code */}
                  <Grid item xs={12} sm={6}>
                    <InfoCard
                      icon={DriverIcons.key}
                      title="Access Code"
                      content={formik.values.AccessCode}
                    />
                  </Grid>

                  {/* PIN */}
                  <Grid item xs={12} sm={6}>
                    <InfoCard icon={DriverIcons.lock} title="PIN" content={formik.values.PIN} />
                  </Grid>
                </Grid>
              </Stack>

              {/* Contact Details */}
              <CardHeader title="Contact Details" />
              <Stack spacing={3} padding={3}>
                <Grid container spacing={2}>
                  {/* Client */}
                  <Grid item xs={12} sm={6}>
                    <InfoCard
                      icon={DriverIcons.group}
                      title="Client"
                      content={formik.values.Client}
                    />
                  </Grid>

                  {/* Haulier */}
                  <Grid item xs={12} sm={6}>
                    <InfoCard
                      icon={DriverIcons.localShipping}
                      title="Haulier"
                      content={formik.values.Haulier}
                    />
                  </Grid>

                  {/* Company */}
                  <Grid item xs={12} sm={6}>
                    <InfoCard
                      icon={DriverIcons.business}
                      title="Company"
                      content={formik.values.CompanyID}
                    />
                  </Grid>
                </Grid>
              </Stack>

              {/* Available */}

              {/* Vehicle and Induction Information */}
              <CardHeader title="Vehicle and Induction Information" />
              <Stack spacing={3} padding={3}>
                <Grid container spacing={2}>
                  {/* Default Vehicle */}
                  <Grid item xs={12} sm={6}>
                    <InfoCard
                      icon={DriverIcons.directionsCar}
                      title="Default Vehicle"
                      content={
                        vehicleType.find((v: any) => v.id === formik.values.DefaultVehicleId)
                          ?.registrationPlate || 'N/A'
                      }
                    />
                  </Grid>

                  {/* Internal/External */}
                  <Grid item xs={12} sm={6}>
                    <InfoCard
                      icon={DriverIcons.home}
                      title="Internal/External"
                      content={formik.values.InternalExternal}
                    />
                  </Grid>

                  {/* Inducted */}
                  <Grid item xs={12} sm={6}>
                    <InfoCard
                      icon={DriverIcons.checkCircle}
                      title="Inducted"
                      content={formik.values.Inducted ? 'Yes' : 'No'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InfoCard
                      icon={DriverIcons.description}
                      title="Description"
                      content={formik.values.Description}
                    />
                  </Grid>
                </Grid>

                {/* Inducted Date and By */}
                {formik.values.Inducted && (
                  <Grid container spacing={2}>
                    {/* Inducted Date */}
                    <Grid item xs={12} sm={6}>
                      <InfoCard
                        icon={DriverIcons.calendarToday}
                        title="Inducted Date"
                        content={moment(formik.values.InductedDate).format('MMMM D, YYYY') || ''}
                      />
                    </Grid>

                    {/* Inducted By */}
                    <Grid item xs={12} sm={6}>
                      <InfoCard
                        icon={DriverIcons.personAdd}
                        title="Inducted By"
                        content={formik.values.InductedBy}
                      />
                    </Grid>
                  </Grid>
                )}
              </Stack>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <FormikProvider value={formik}>
          <Form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ p: 3 }}>
                  <Stack spacing={4}>
                    {/* General Information */}
                    <Typography variant="h6" gutterBottom>
                      General Information
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        fullWidth
                        label="Full Name*"
                        {...getFieldProps('FullName')}
                        error={Boolean(touched.FullName && errors.FullName)}
                        helperText={touched.FullName && errors.FullName}
                      />
                      <TextField
                        fullWidth
                        label="Weighbridge Access PIN*"
                        {...getFieldProps('PIN')}
                        error={Boolean(touched.PIN && errors.PIN)}
                        helperText={touched.PIN && errors.PIN}
                      />
                      <TextField
                        fullWidth
                        label="Mobile"
                        {...getFieldProps('Mobile')}
                        error={Boolean(touched.Mobile && errors.Mobile)}
                        helperText={touched.Mobile && errors.Mobile}
                      />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        fullWidth
                        label="Email"
                        {...getFieldProps('Email')}
                        error={Boolean(touched.Email && errors.Email)}
                        helperText={touched.Email && errors.Email}
                      />
                      <TextField
                        fullWidth
                        type="number"
                        label="Employee Number"
                        {...getFieldProps('PersonnelId')}
                        error={Boolean(touched.PersonnelId && errors.PersonnelId)}
                        helperText={touched.PersonnelId && errors.PersonnelId}
                      />
                      <TextField
                        fullWidth
                        label="Access Code"
                        {...getFieldProps('AccessCode')}
                        error={Boolean(touched.AccessCode && errors.AccessCode)}
                        helperText={touched.AccessCode && errors.AccessCode}
                      />
                    </Stack>
                    {/* Vehicle and Induction Information */}
                    <Typography variant="h6" gutterBottom>
                      Vehicle and Induction Information
                    </Typography>
                    <Stack direction="row" spacing={3}>
                      <FormControl
                        fullWidth
                        error={Boolean(touched.DefaultVehicleId && errors.DefaultVehicleId)}
                      >
                        <Autocomplete
                          options={vehicleType}
                          disabled={!formik.values.CompanyID || vehicleType.length === 0}
                          getOptionLabel={(option: any) => option.registrationPlate}
                          filterOptions={(options, { inputValue }) =>
                            options.filter(
                              (option) =>
                                option.registrationPlate
                                  .toLowerCase()
                                  .includes(inputValue.toLowerCase()) ||
                                option.description.toLowerCase().includes(inputValue.toLowerCase())
                            )
                          }
                          renderOption={(props, option: any) => (
                            <li {...props} key={`${option.id}-${option.registrationPlate}`}>
                              <div>
                                <strong>{option?.registrationPlate || 'N/A'}</strong>
                                <br />
                                <small
                                  style={{ fontSize: '0.675rem', fontWeight: 400, color: 'gray' }}
                                >
                                  {option?.description || 'No description available'}
                                </small>
                              </div>
                            </li>
                          )}
                          value={
                            vehicleType?.find(
                              (type: any) => type.id === formik.values.DefaultVehicleId
                            ) || null
                          }
                          onChange={(event, value) =>
                            formik.setFieldValue('DefaultVehicleId', value?.id || null)
                          }
                          renderInput={(params) => (
                            <div>
                              <TextField
                                {...params}
                                label="Default Vehicle"
                                error={Boolean(touched.DefaultVehicleId && errors.DefaultVehicleId)}
                                helperText={touched.DefaultVehicleId && errors.DefaultVehicleId}
                              />
                              {formik.values.DefaultVehicleId && (
                                <Typography variant="body2" style={{ color: 'gray', marginTop: 4 }}>
                                  {vehicleType.find(
                                    (v: any) => v.id === formik.values.DefaultVehicleId
                                  )?.description || 'No description available'}
                                </Typography>
                              )}
                            </div>
                          )}
                        />
                      </FormControl>

                      <FormControl sx={{ width: '100%' }}>
                        <InputLabel>Internal/External*</InputLabel>
                        <Select
                          name="InternalExternal" // Ensure this matches the key in initialValues
                          value={formik.values.InternalExternal} // Bind to Formik's value
                          onChange={formik.handleChange} // Use Formik's change handler
                          label="Internal/External"
                        >
                          <MenuItem value="I">Internal</MenuItem>
                          <MenuItem value="E">External</MenuItem>
                        </Select>
                        <FormHelperText>
                          {formik.touched.InternalExternal && formik.errors.InternalExternal}
                        </FormHelperText>
                      </FormControl>
                      <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend">Inducted*</FormLabel>
                        <RadioGroup
                          row
                          {...getFieldProps('Inducted')}
                          name="Inducted"
                          value={formik.values.Inducted ? 'true' : 'false'} // Ensure the value is 'true' or 'false' as strings
                          onChange={(e) =>
                            formik.setFieldValue('Inducted', e.target.value === 'true')
                          }
                        >
                          <FormControlLabel value="true" control={<Radio />} label="Yes" />
                          <FormControlLabel value="false" control={<Radio />} label="No" />
                        </RadioGroup>
                        {touched.Inducted && errors.Inducted && (
                          <FormHelperText error>{errors.Inducted}</FormHelperText>
                        )}
                      </FormControl>
                    </Stack>

                    {/* Contact Details */}
                    <Typography variant="h6" gutterBottom>
                      Contact Details
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <FormControl fullWidth error={Boolean(touched.CompanyID && errors.CompanyID)}>
                        <Autocomplete
                          options={contacts}
                          getOptionLabel={(option: any) => option.value} // Show only value in the input field
                          filterOptions={(options, { inputValue }) =>
                            options.filter(
                              (option) =>
                                option.value.toLowerCase().includes(inputValue.toLowerCase()) ||
                                option.accountRef.toLowerCase().includes(inputValue.toLowerCase())
                            )
                          }
                          renderOption={(props, option: any) => (
                            <li {...props} key={`${option.id}-${option.value}`}>
                              <div>
                                <strong>{option?.value || 'N/A'}</strong>
                                <br />
                                <Typography
                                  component="span"
                                  variant="body2"
                                  style={{ color: 'gray' }}
                                >
                                  {option?.accountRef || 'N/A'}
                                </Typography>
                              </div>
                            </li>
                          )}
                          value={
                            contacts?.find((type: any) => type.id === formik.values.CompanyID) ||
                            null
                          }
                          onChange={(event, value) => {
                            fetchVehiclesByOwner(value?.value || '');
                            formik.setFieldValue(
                              'CompanyID',
                              value?.id !== undefined ? parseInt(value.id, 10) : null
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Owner"
                              error={Boolean(touched.CompanyID && errors.CompanyID)}
                              helperText={touched.CompanyID && errors.CompanyID}
                            />
                          )}
                        />

                        {/* Display AccountRef Below TextField After Selection */}
                        {formik.values.CompanyID && (
                          <Typography variant="body2" style={{ color: 'gray', marginTop: 4 }}>
                            {contacts.find((c: any) => c.id === formik.values.CompanyID)
                              ?.accountRef || 'N/A'}
                          </Typography>
                        )}
                      </FormControl>
                      <FormControl fullWidth error={Boolean(touched.Client && errors.Client)}>
                        <Autocomplete
                          options={client}
                          getOptionLabel={(option: any) => option.value}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          filterOptions={(options, { inputValue }) =>
                            options.filter(
                              (option) =>
                                option.value.toLowerCase().includes(inputValue.toLowerCase()) ||
                                option.accountRef.toLowerCase().includes(inputValue.toLowerCase())
                            )
                          }
                          renderOption={(props, option: any) => (
                            <li {...props} key={`${option.id}-${option.value}`}>
                              <div>
                                <strong>{option?.value || 'N/A'}</strong>
                                <br />
                                <small
                                  style={{ fontSize: '0.675rem', fontWeight: 400, color: 'gray' }}
                                >
                                  {option?.accountRef || 'N/A'}
                                </small>
                              </div>
                            </li>
                          )}
                          value={
                            client?.find((type: any) => type.value === formik.values.Client) || null
                          }
                          onChange={(event, value) => {
                            formik.setFieldValue('Client', value?.value || '');
                            formik.setFieldValue('ClientId', value?.id || null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Client"
                              error={Boolean(touched.Client && errors.Client)}
                              helperText={touched.Client && errors.Client}
                            />
                          )}
                        />

                        {/* Display AccountRef Below TextField After Selection */}
                        {formik.values.ClientId && (
                          <Typography variant="body2" style={{ color: 'gray', marginTop: 4 }}>
                            {client.find((c: any) => c.id === formik.values.ClientId)?.accountRef ||
                              'N/A'}
                          </Typography>
                        )}
                      </FormControl>
                      <FormControl fullWidth error={Boolean(touched.Haulier && errors.Haulier)}>
                        <Autocomplete
                          options={haulier}
                          getOptionLabel={(option: any) => option.value}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          filterOptions={(options, { inputValue }) =>
                            options.filter(
                              (option) =>
                                option.value.toLowerCase().includes(inputValue.toLowerCase()) ||
                                option.accountRef.toLowerCase().includes(inputValue.toLowerCase())
                            )
                          }
                          renderOption={(props, option: any) => (
                            <li {...props} key={`${option.id}-${option.value}`}>
                              <div>
                                <strong>{option?.value || 'N/A'}</strong>
                                <br />
                                <small
                                  style={{ fontSize: '0.675rem', fontWeight: 400, color: 'gray' }}
                                >
                                  {option?.accountRef || 'N/A'}
                                </small>
                              </div>
                            </li>
                          )}
                          value={
                            haulier?.find((type: any) => type.value === formik.values.Haulier) ||
                            null
                          }
                          onChange={(event, value) => {
                            formik.setFieldValue('Haulier', value?.value || '');
                            formik.setFieldValue('HaulierId', value?.id || null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Haulier"
                              error={Boolean(touched.Haulier && errors.Haulier)}
                              helperText={touched.Haulier && errors.Haulier}
                            />
                          )}
                        />

                        {/* Display AccountRef Below TextField After Selection */}
                        {formik.values.HaulierId && (
                          <Typography variant="body2" style={{ color: 'gray', marginTop: 4 }}>
                            {haulier.find((c: any) => c.id === formik.values.HaulierId)
                              ?.accountRef || 'N/A'}
                          </Typography>
                        )}
                      </FormControl>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                      <FormControl
                        fullWidth
                        error={Boolean(touched.defaultCompanyId && errors.defaultCompanyId)}
                      >
                        <Autocomplete
                          options={companies}
                          getOptionLabel={(option: any) => option.value} // Show only value in the input field
                          filterOptions={(options, { inputValue }) =>
                            options.filter(
                              (option) =>
                                option.value.toLowerCase().includes(inputValue.toLowerCase()) ||
                                option.accountRef.toLowerCase().includes(inputValue.toLowerCase())
                            )
                          }
                          renderOption={(props, option: any) => (
                            <li {...props} key={`${option.id}-${option.value}`}>
                              <div>
                                <strong>{option?.value || 'N/A'}</strong>
                                <br />
                              </div>
                            </li>
                          )}
                          value={
                            companies?.find(
                              (type: any) => type.id === formik.values.defaultCompanyId
                            ) || null
                          }
                          onChange={(event, value) => {
                            fetchVehiclesByOwner(value?.value || '');
                            formik.setFieldValue(
                              'defaultCompanyId',
                              value?.id !== undefined ? parseInt(value.id, 10) : null
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Company"
                              required={formik.values.InternalExternal === 'I'}
                              error={Boolean(touched.defaultCompanyId && errors.defaultCompanyId)}
                              helperText={touched.defaultCompanyId && errors.defaultCompanyId}
                            />
                          )}
                        />
                      </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      <FormControl>
                        <FormLabel component="legend">Available*</FormLabel>
                        <RadioGroup
                          row
                          {...getFieldProps('Available')}
                          name="Available"
                          value={
                            formik.values.Available
                              ? 'true'
                              : !formik.values.Available
                              ? 'false'
                              : null
                          } // Ensure the value is 'true' or 'false' as strings
                          onChange={(e) =>
                            formik.setFieldValue('Available', e.target.value === 'true')
                          }
                        >
                          <FormControlLabel value={'true'} control={<Radio />} label="Yes" />
                          <FormControlLabel value={'false'} control={<Radio />} label="No" />
                        </RadioGroup>
                        {touched.Available && errors.Available && (
                          <FormHelperText error>{errors.Available}</FormHelperText>
                        )}
                      </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                      {formik.values.Inducted && (
                        <DatePicker
                          label="Inducted Date*"
                          {...getFieldProps('InductedDate')}
                          value={formik.values.InductedDate} // bind value to formik
                          onChange={(date) => formik.setFieldValue('InductedDate', date || null)} // update Formik on date change
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              sx={{ width: '100%' }}
                              error={Boolean(touched.InductedDate && errors.InductedDate)}
                              helperText={touched.InductedDate && errors.InductedDate}
                            />
                          )} // render TextField for proper styling
                        />
                      )}
                      {formik.values.Inducted && (
                        <TextField
                          fullWidth
                          label="Inducted By*"
                          {...getFieldProps('InductedBy')}
                          error={Boolean(touched.InductedBy && errors.InductedBy)}
                          helperText={touched.InductedBy && errors.InductedBy}
                        />
                      )}
                    </Stack>

                    {/* Address Information */}
                    <Typography variant="h6" gutterBottom>
                      Additional Information
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={4}
                        {...getFieldProps('Description')}
                        error={Boolean(touched.Description && errors.Description)}
                        helperText={touched.Description && errors.Description}
                      />
                    </Stack>
                    {isDetails ? null : (
                      <>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formik.values.ignoreDuplicates}
                              onChange={(e) =>
                                formik.setFieldValue('ignoreDuplicates', e.target.checked)
                              }
                              color="primary"
                            />
                          }
                          label="Ignore duplicates"
                        />
                        <Stack direction="row" justifyContent="flex-end" spacing={2}>
                          <LoadingButton
                            type="submit"
                            variant="contained"
                            loading={isSubmitting}
                            disabled={
                              !isEdit && // If it's not in edit mode
                              ((!isDetails && !formik.isValid) || // If validation is required and form is invalid
                                formik.isSubmitting ||
                                Object.keys(formik.errors).length > 0)
                            }
                          >
                            {!isEdit ? 'Create Driver' : 'Save Changes'}
                          </LoadingButton>
                        </Stack>
                      </>
                    )}
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      )}
    </>
  );
}
